// src/pages/settings/appointments/appointmentTypeComponents/components/StepEditorDrawer.tsx

import React, { useState, useEffect } from "react";
import { Input, Button, Select, InputNumber, Tag, Typography, Alert, Row, Col, Space, Card, message } from "antd";
import { ContrastTag } from "../../../../../components/shared";
import { CopyOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { WorkflowStep, AvailableDataSource } from "../../../../../types/appointments/ConsultWorkflowTypes";
import { SchemaNode, jsonSchemaToSchemaNode } from "../../../../../types/appointments/SchemaBuilderTypes";
import SchemaBuilder from "../../../../../components/schemaBuilder/SchemaBuilder";
import DetailDrawer from "../../../../../components/shared/DetailDrawer";

const { TextArea } = Input;
const { Text } = Typography;

interface StepEditorDrawerProps {
	visible: boolean;
	step?: WorkflowStep;
	existingTopics: string[];
	allSteps: WorkflowStep[];
	selectedDataSources: string[];
	availableDataSources: AvailableDataSource[];
	onSubmit: (values: WorkflowStep) => void;
	onClose: () => void;
}

const UpdatedStepEditorDrawer: React.FC<StepEditorDrawerProps> = ({ visible, step, existingTopics, allSteps, selectedDataSources, availableDataSources, onSubmit, onClose }) => {
	const [stepData, setStepData] = useState<WorkflowStep>({
		step_key: "",
		step_order: 0,
		title: "",
		description: "",
		prompt_template: "",
		parallel_group: undefined,
		time_allocation_minutes: undefined,
		dependencies: [],
		required_data_sources: [],
		output_schema: {},
		section: "",
		is_parallel_eligible: false,
	});

	const [schemaNodes, setSchemaNodes] = useState<SchemaNode[]>([]);
	const [activeTab, setActiveTab] = useState<string>("basic");
	const [schemaKey, setSchemaKey] = useState<string>("");

	useEffect(() => {
		if (step) {

			setStepData({
				id: step.id,
				workflow_template_id: step.workflow_template_id,
				step_key: step.step_key,
				step_order: step.step_order,
				title: step.title || "",
				description: step.description || "",
				prompt_template: step.prompt_template || "",
				parallel_group: step.parallel_group,
				time_allocation_minutes: step.time_allocation_minutes,
				dependencies: step.dependencies || [],
				required_data_sources: step.required_data_sources || [],
				output_schema: step.output_schema || {},
				section: step.section,
				is_parallel_eligible: step.is_parallel_eligible,
			});

			// Parse schema to nodes
			if (step.output_schema && Object.keys(step.output_schema).length > 0) {
				try {
					const nodes = jsonSchemaToSchemaNode(step.output_schema);
					setSchemaNodes(nodes);
				} catch (error) {
					console.error("Failed to parse output schema:", error);
					setSchemaNodes([]);
				}
			} else {
				setSchemaNodes([]);
			}

			// Force SchemaBuilder remount with new key
			setSchemaKey(`${step.id || "new"}_${Date.now()}`);
		} else {
			// New step
			setStepData({
				step_key: "",
				step_order: 0,
				title: "",
				description: "",
				prompt_template: "",
				parallel_group: undefined,
				time_allocation_minutes: undefined,
				dependencies: [],
				required_data_sources: [],
				output_schema: {},
				section: "",
				is_parallel_eligible: false,
			});
			setSchemaNodes([]);
			setSchemaKey(`new_${Date.now()}`);
		}
	}, [step, visible]);

	const updateStepField = <K extends keyof WorkflowStep>(field: K, value: WorkflowStep[K]) => {
		setStepData((prev) => ({ ...prev, [field]: value }));
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
		message.success("Copied!");
	};

	const dataSourceVariables = availableDataSources
		.filter((source) => selectedDataSources.includes(source.identifier))
		.map((source) => ({
			label: source.name,
			variable: source.variable_name,
			category: "Data Source",
		}));

	const stepVariables = allSteps
		.filter((s) => s.title !== step?.title)
		.map((s) => ({
			label: s.title,
			variable: `step:${s.step_key}`,
			category: "Step Output",
		}));

	const allVariables = [...dataSourceVariables, ...stepVariables];

	const handleSchemaChange = (nodes: SchemaNode[], jsonSchema: any) => {
		setSchemaNodes(nodes);
		setStepData((prev) => ({
			...prev,
			output_schema: jsonSchema,
		}));
	};

	const handleSubmit = () => {
		if (!stepData.title) {
			message.error("Step name is required");
			setActiveTab("basic");
			return;
		}
		if (!stepData.step_key) {
			message.error("Step key is required");
			setActiveTab("basic");
			return;
		}
		if (!stepData.prompt_template) {
			message.error("Prompt template is required");
			setActiveTab("prompt");
			return;
		}

		// Ensure output_schema is properly set
		if (!stepData.output_schema || Object.keys(stepData.output_schema).length === 0) {
			message.error("Output schema is required - add at least one field");
			setActiveTab("schema");
			return;
		}

		onSubmit(stepData);
	};

	return (
		<DetailDrawer
			open={visible}
			onClose={onClose}
			width="90vw"
			title={step ? "Edit Step" : "Add Step"}
			subtitle={step?.step_key ? <ContrastTag background="var(--mg-info)" style={{ fontSize: 11 }}>{step.step_key}</ContrastTag> : undefined}
			footer={
				<Space>
					{stepData.output_schema && Object.keys(stepData.output_schema.properties || {}).length > 0 && (
						<Text type="secondary" style={{ fontSize: 11, marginRight: 8 }}>
							Schema: {Object.keys(stepData.output_schema.properties).length} root fields
						</Text>
					)}
					<Button onClick={onClose}>Cancel</Button>
					<Button type="primary" onClick={handleSubmit}>
						{step ? "Update Step" : "Add Step"}
					</Button>
				</Space>
			}
			tabs={[
				{
					key: "basic",
					label: "Basic Info",
					children: (
						<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
							<Alert
								message="Step Configuration"
								description="Configure the basic properties of this workflow step. The step key should be unique and used for referencing this step's output in later steps."
								type="info"
								icon={<InfoCircleOutlined />}
								showIcon
								style={{ fontSize: 12 }}
							/>

							<Row gutter={[16, 0]}>
								<Col span={12}>
									<div>
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
											Step Key <span style={{ color: "var(--mg-danger)" }}>*</span>
										</Text>
										<Input
											placeholder="e.g., patient_background"
											value={stepData.step_key}
											onChange={(e) => updateStepField("step_key", e.target.value)}
										/>
									</div>
								</Col>
								<Col span={12}>
									<div>
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
											Title <span style={{ color: "var(--mg-danger)" }}>*</span>
										</Text>
										<Input
											placeholder="e.g., Patient Background"
											value={stepData.title}
											onChange={(e) => updateStepField("title", e.target.value)}
										/>
									</div>
								</Col>
							</Row>

							<Row gutter={[16, 0]}>
								<Col span={12}>
									<div>
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Parallel Group</Text>
										<InputNumber
											min={1}
											max={10}
											placeholder="Optional"
											style={{ width: "100%" }}
											value={stepData.parallel_group}
											onChange={(value) => updateStepField("parallel_group", value || undefined)}
										/>
									</div>
								</Col>
								<Col span={12}>
									<div>
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Time Allocation (minutes)</Text>
										<InputNumber
											min={1}
											placeholder="Optional"
											style={{ width: "100%" }}
											value={stepData.time_allocation_minutes}
											onChange={(value) => updateStepField("time_allocation_minutes", value || undefined)}
										/>
									</div>
								</Col>
							</Row>

							<div>
								<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Description</Text>
								<TextArea
									rows={2}
									placeholder="Brief description of what this step does"
									value={stepData.description}
									onChange={(e) => updateStepField("description", e.target.value)}
								/>
							</div>

							<Row gutter={[16, 0]}>
								<Col span={12}>
									<div>
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Section</Text>
										<Input
											placeholder="e.g., Assessment, Recommendations"
											value={stepData.section}
											onChange={(e) => updateStepField("section", e.target.value)}
										/>
									</div>
								</Col>
								<Col span={12}>
									<div>
										<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Dependencies (Step Keys)</Text>
										<Select
											mode="multiple"
											placeholder="Select step keys this depends on"
											options={allSteps
												.filter((t) => t.step_key !== step?.step_key)
												.map((s) => ({ label: `${s.title} (${s.step_key})`, value: s.step_key }))}
											style={{ width: "100%" }}
											value={stepData.dependencies}
											onChange={(value) => updateStepField("dependencies", value)}
										/>
									</div>
								</Col>
							</Row>
						</div>
					),
				},
				{
					key: "prompt",
					label: "Prompt Template",
					children: (
						<div style={{ display: "flex", height: "calc(100vh - 300px)" }}>
							{/* Variables Sidebar */}
							<div style={{ width: 220, borderRight: "1px solid var(--mg-border)", padding: 12, overflowY: "auto", flexShrink: 0 }}>
								<Text strong style={{ fontSize: 12, display: "block", marginBottom: 4 }}>Available Variables</Text>
								<Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 12 }}>
									Click to copy. Use {"{{variable}}"} in prompt.
								</Text>

								{allVariables.length === 0 ? (
									<Alert
										message="No variables available"
										description="Add data sources or create previous steps to use variables."
										type="info"
										showIcon
										style={{ fontSize: 11 }}
									/>
								) : (
									<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
										{dataSourceVariables.length > 0 && (
											<div>
												<Text type="secondary" style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
													Data Sources
												</Text>
												<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
													{dataSourceVariables.map((v) => (
														<Card
															key={v.variable}
															size="small"
															style={{ cursor: "pointer", border: "1px solid var(--mg-border)" }}
															styles={{ body: { padding: "6px 8px" } }}
															onClick={() => copyToClipboard(`{{${v.variable}}}`)}
														>
															<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
																<div>
																	<Text strong style={{ fontSize: 11 }}>{v.label}</Text>
																	<div style={{ marginTop: 2 }}>
																		<ContrastTag background="var(--mg-info)" style={{ fontSize: 10 }}>{`{{${v.variable}}}`}</ContrastTag>
																	</div>
																</div>
																<CopyOutlined style={{ fontSize: 11, color: "var(--mg-primary)" }} />
															</div>
														</Card>
													))}
												</div>
											</div>
										)}
										{stepVariables.length > 0 && (
											<div>
												<Text type="secondary" style={{ fontSize: 11, fontWeight: 600, display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
													Step Outputs
												</Text>
												<div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
													{stepVariables.map((v) => (
														<Card
															key={v.variable}
															size="small"
															style={{ cursor: "pointer", border: "1px solid var(--mg-border)" }}
															styles={{ body: { padding: "6px 8px" } }}
															onClick={() => copyToClipboard(`{{${v.variable}}}`)}
														>
															<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
																<div>
																	<Text strong style={{ fontSize: 11 }}>{v.label}</Text>
																	<div style={{ marginTop: 2 }}>
																		<ContrastTag background="var(--mg-success)" style={{ fontSize: 10 }}>{`{{${v.variable}}}`}</ContrastTag>
																	</div>
																</div>
																<CopyOutlined style={{ fontSize: 11, color: "var(--mg-success)" }} />
															</div>
														</Card>
													))}
												</div>
											</div>
										)}
									</div>
								)}
							</div>

							{/* Prompt Editor */}
							<div style={{ flex: 1, padding: "0 16px 16px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
								<Alert
									message="Writing Prompts"
									description="Use variables from the sidebar to access data. Write clear instructions for the AI about what to analyse and generate. Be specific about format, tone, and required fields."
									type="info"
									showIcon
									style={{ fontSize: 12 }}
								/>
								<div style={{ flex: 1 }}>
									<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 6 }}>
										Prompt Template <span style={{ color: "var(--mg-danger)" }}>*</span>
									</Text>
									<TextArea
										style={{ fontFamily: "monospace", fontSize: 12 }}
										rows={20}
										value={stepData.prompt_template}
										onChange={(e) => updateStepField("prompt_template", e.target.value)}
										placeholder="Enter AI prompt. Use {{variable}} syntax to reference data sources and previous step outputs."
									/>
								</div>
							</div>
						</div>
					),
				},
				{
					key: "schema",
					label: "Output Schema",
					children: (
						<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
							<Alert
								message={
									<div>
										<Text strong style={{ fontSize: 12 }}>Schema Builder</Text>
										<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
											Build the output structure the AI will populate. Add <strong>descriptions</strong> to each field to guide what it should contain.
										</Text>
									</div>
								}
								type="info"
								showIcon
							/>
							<SchemaBuilder key={schemaKey} initialSchema={schemaNodes} onChange={handleSchemaChange} />
						</div>
					),
				},
			]}
			activeTab={activeTab}
			onTabChange={setActiveTab}
		/>
	);
};

export default UpdatedStepEditorDrawer;
			